import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  SetStateAction,
  Dispatch,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useCompleteEnrollCourseMutation,
  useEnrollCourseMutation,
  useGetCourseDetailsQuery,
  useGetCourseTopicsQuery,
  useGetAllCourseQuestionsQuery,
  useSubmitCourseAttemptMutation,
  useTrackCourseProgressMutation,
} from '../redux/apiSlice';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';

// localStorage utility functions for explain highlighted code usage
const EXPLAIN_HIGHLIGHTED_STORAGE_KEY = 'explainHighlightedUsage';

// sessionStorage utility functions for user input
export const USER_INPUT_STORAGE_KEY = 'userInput';

interface ExplainHighlightedUsage {
  [questionId: number]: number;
}

const getExplainHighlightedUsage = (): ExplainHighlightedUsage => {
  try {
    const stored = localStorage.getItem(EXPLAIN_HIGHLIGHTED_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error(
      'Error reading explain highlighted usage from localStorage:',
      error,
    );
    return {};
  }
};

const setExplainHighlightedUsage = (usage: ExplainHighlightedUsage): void => {
  try {
    localStorage.setItem(
      EXPLAIN_HIGHLIGHTED_STORAGE_KEY,
      JSON.stringify(usage),
    );
  } catch (error) {
    console.error(
      'Error saving explain highlighted usage to localStorage:',
      error,
    );
  }
};

const incrementExplainHighlightedUsage = (questionId: number): number => {
  const usage = getExplainHighlightedUsage();
  const currentCount = usage[questionId] || 0;
  const newCount = currentCount + 1;
  usage[questionId] = newCount;
  setExplainHighlightedUsage(usage);
  return newCount;
};

const getExplainHighlightedUsageForQuestion = (questionId: number): number => {
  const usage = getExplainHighlightedUsage();
  return usage[questionId] || 0;
};

const resetExplainHighlightedUsage = (): void => {
  try {
    localStorage.removeItem(EXPLAIN_HIGHLIGHTED_STORAGE_KEY);
  } catch (error) {
    console.error(
      'Error resetting explain highlighted usage from localStorage:',
      error,
    );
  }
};

// sessionStorage utility functions for user input
const saveUserInputToSession = (input: string, questionId: number): void => {
  try {
    sessionStorage.setItem(`${USER_INPUT_STORAGE_KEY}-${questionId}`, input);
  } catch (error) {
    console.error('Error saving user input to sessionStorage:', error);
  }
};

const getUserInputFromSession = (questionId: number): string => {
  try {
    return (
      sessionStorage.getItem(`${USER_INPUT_STORAGE_KEY}-${questionId}`) || ''
    );
  } catch (error) {
    console.error('Error reading user input from sessionStorage:', error);
    return '';
  }
};

const clearUserInputFromSession = (): void => {
  try {
    console.log('clearing user input from session');

    // Get all session storage keys
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(USER_INPUT_STORAGE_KEY)) {
        keysToRemove.push(key);
      }
    }

    // Remove all keys that start with USER_INPUT_STORAGE_KEY
    keysToRemove.forEach((key) => {
      sessionStorage.removeItem(key);
    });

    console.log(`Cleared ${keysToRemove.length} user input session items`);
  } catch (error) {
    console.error('Error clearing user input from sessionStorage:', error);
  }
};

// Define types for our context
interface SectionDetails {
  id: number;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  section_id: number;
  title: string;
  description: string;
  order: number;
  estimated_duration: string | null;
  course: number;
}

export interface QuestionDetails {
  id: number;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  question_id: number;
  prompt: string;
  provided_code: string;
  explanation: string;
  tip: string;
  expected_output: string;
  is_typing_course: boolean;
  ai_validation_required: boolean;
  order_in_section: number;
  is_final_activity: boolean;
  section: SectionDetails;
  course: number;
  compare_mode: number;
  library?: string | undefined; // Array of Python packages to install
  // SQL specific fields
  data?: string | undefined;
  sql_schema?: {
    tables: {
      name: string;
      columns: {
        name: string;
        type: string;
        constraints?: string[];
      }[];
      data?: any[][]; // Sample data for the table
    }[];
  };
  detail?: {
    attempted_code?: string;
    words_per_minute?: number;
    attempt_count?: number;
    ai_usage?: boolean;
    is_correct?: boolean;
  };
}

// Interface for attempt tracking
interface AttemptTracking {
  debugHelpCount: number;
  explainHelpCount: number;
  explainHighlightedCount: number;
  attemptCount: number;
  isCorrect: boolean;
  isCompleted: boolean;
  wpm: number | null;
  isSubmitted: boolean;
}

// Interface for popup message
interface PopupMessage {
  isOpen: boolean;
  message: string;
  onClose?: () => void;
  onConfirm?: () => void;
  confirmText?: string;
}

interface CourseContextType {
  // Course info
  courseLoading: boolean;
  courseId: number;
  courseName: string;
  courseDuration: string;
  progress: number;
  language: string;
  timeSpent: number;

  // Current state
  currentQuestionId: number | undefined;
  allQuestions: QuestionDetails[] | undefined;
  currentQuestionIndex: number;
  isLastQuestion: boolean;
  userInput: string;
  // Loading states
  loading: boolean;

  // Attempt tracking
  updateProgressTimeSpent: (currentTimeInMinutes: number) => void;
  attemptTracking: AttemptTracking;
  incrementDebugHelp: () => void;
  incrementExplainHelp: () => void;
  incrementExplainHighlightedHelp: () => void;
  updateAttemptStatus: (isCorrect: boolean, wpm?: number | null) => void;
  resetAttemptTracking: () => void;
  resetExplainHighlightedUsageData: () => void;
  clearUserInputFromSession: () => void;

  // Popup functionality
  popupMessage: PopupMessage;
  showPopupMessage: (
    message: string,
    onConfirm?: () => void,
    confirmText?: string,
  ) => void;
  hidePopupMessage: () => void;

  // Actions
  submitAttempt: (isCorrect: boolean, wpm?: number | null) => Promise<void>;
  completeQuestion: () => Promise<void>;
  nextQuestion: () => void;
  setProgress: (progress: number) => void;
  setTimeSpent: (time: number) => void;
  setCurrentQuestionId: (id: number | undefined) => void;
  setUserInput: (input: string) => void;
}

// Default attempt tracking state
const defaultAttemptTracking: AttemptTracking = {
  debugHelpCount: 0,
  explainHelpCount: 0,
  explainHighlightedCount: 0,
  attemptCount: 0,
  isCorrect: false,
  isCompleted: false,
  wpm: null,
  isSubmitted: false,
};

// Default popup message state
const defaultPopupMessage: PopupMessage = {
  isOpen: false,
  message: '',
};

// Create context with default values
const defaultContextValue: CourseContextType = {
  // Course info
  courseLoading: false,
  courseId: 1,
  courseName: 'Default Course',
  courseDuration: '2h 30m',
  progress: 0,
  language: 'javascript',
  timeSpent: 0,

  // Current state
  currentQuestionId: undefined,
  allQuestions: undefined,
  currentQuestionIndex: 0,
  isLastQuestion: false,
  userInput: '',
  // Loading states
  loading: false,

  // Attempt tracking
  attemptTracking: defaultAttemptTracking,
  updateProgressTimeSpent: () => {
    /* intentionally empty */
  },
  incrementDebugHelp: () => {
    /* intentionally empty */
  },
  incrementExplainHelp: () => {
    /* intentionally empty */
  },
  incrementExplainHighlightedHelp: () => {
    /* intentionally empty */
  },
  updateAttemptStatus: () => {
    /* intentionally empty */
  },
  resetAttemptTracking: () => {
    /* intentionally empty */
  },
  resetExplainHighlightedUsageData: () => {
    /* intentionally empty */
  },
  clearUserInputFromSession: () => {
    /* intentionally empty */
  },

  // Popup functionality
  popupMessage: defaultPopupMessage,
  showPopupMessage: () => {
    /* intentionally empty */
  },
  hidePopupMessage: () => {
    /* intentionally empty */
  },

  // Actions
  submitAttempt: async () => {
    /* intentionally empty */
  },
  completeQuestion: async () => {
    /* intentionally empty */
  },
  nextQuestion: () => {
    /* intentionally empty */
  },
  setProgress: (_progress: number) => {
    /* intentionally empty */
  },
  setTimeSpent: (_time: number) => {
    /* intentionally empty */
  },
  setCurrentQuestionId: (_id: number | undefined) => {
    /* intentionally empty */
  },
  setUserInput: (_input: string) => {
    /* intentionally empty */
  },
};

// Create the context
const CourseContext = createContext<CourseContextType>(defaultContextValue);

// Create a provider component
export const CourseProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [enrollCourse] = useEnrollCourseMutation();
  // const [completeEnrollCourse] = useCompleteEnrollCourseMutation();

  const { user } = useSelector((state: RootState) => state.app.user);
  const {
    courseId,
    // duration: courseDuration = '2h 30m',
    // courseName = 'Default Course',
    // language = 'javascript',
    // // lastAttemptedSectionId,
    // // lastAttemptedQuestionId,
    // lastAttemptedQuestionAttempts,
    // nextCourseId,
  } = location.state || {};

  // API hooks
  const [trackCourseProgress] = useTrackCourseProgressMutation();
  const [submitCourseAttempt] = useSubmitCourseAttemptMutation();

  // const [courseDuration, setCourseDuration] = useState<string>('2h 30m');
  const [progress, setProgress] = useState<number>();
  const [timeSpent, setTimeSpent] = useState<number>();
  const [userInput, setUserInput] = useState<string>('');

  // Current state
  // const [currentSectionId, setCurrentSectionId] = useState<number | undefined>(
  //   // lastAttemptedSectionId > 0 ? lastAttemptedSectionId : undefined,
  //   undefined,
  // );
  const [currentQuestionId, setCurrentQuestionId] = useState<
    number | undefined
  >(
    // lastAttemptedQuestionId > 0 ? lastAttemptedQuestionId : undefined,
    undefined,
  );

  // Attempt tracking state
  const [attemptTracking, setAttemptTracking] = useState<AttemptTracking>(
    defaultAttemptTracking,
  );

  // Popup message state
  const [popupMessage, setPopupMessage] =
    useState<PopupMessage>(defaultPopupMessage);

  const {
    data: courseData,
    isLoading: courseLoading,
    error: courseError,
    isSuccess: isCourseSuccess,
  } = useGetCourseDetailsQuery(courseId, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  // Fetch all questions with section details for this course
  const { data: allQuestions, isLoading: questionsLoading } =
    useGetAllCourseQuestionsQuery(courseId, {
      skip: !courseId,
    });

  // Redirect to courses page if no courseId provided
  useEffect(() => {
    if (!courseId && !user) {
      return navigate(`/courses`);
    }
    let progress = 0;
    let timeSpent = 0;
    if (courseData && !courseLoading) {
      if (courseData.detail) {
        setCurrentQuestionId(
          courseData?.detail?.last_incomplete_question_id > 0
            ? courseData?.detail?.last_incomplete_question_id
            : allQuestions && allQuestions?.length > 0
              ? allQuestions[0].id
              : undefined,
        );
        progress =
          courseData.detail?.total_questions > 0
            ? Math.round(
                (courseData.detail?.total_correct_questions /
                  courseData.detail?.total_questions) *
                  100,
              )
            : 0;

        const minutes = courseData.detail?.time_spent;
        if (minutes) {
          timeSpent = minutes * 60;
        }
      }
      setProgress(progress);
      setTimeSpent(timeSpent);
      if (progress === 0) {
        enrollCourse({ course: courseId }).unwrap();
      }
    }
    // const savedInput = getUserInputFromSession();
    // if (savedInput) {
    //   setUserInput(savedInput);
    // }
    return () => {
      setProgress(0);
      setTimeSpent(0);
      // setUserInput('');
      setAttemptTracking(defaultAttemptTracking);
      setCurrentQuestionId(undefined);
      // Clear session storage on cleanup
    };
  }, [courseId, user, allQuestions, courseData]);

  // Find the current question index within the section
  const currentQuestionIndex = useMemo(() => {
    if (!allQuestions || allQuestions.length === 0) {
      return 0;
    }
    const index = allQuestions.findIndex((q) => q.id === currentQuestionId);
    return index >= 0 ? index : 0;
  }, [allQuestions, currentQuestionId]);

  // Enhanced setUserInput function that also saves to session storage
  const setUserInputWithSession = useCallback(
    (input: string) => {
      setUserInput(input);
      const questionId = allQuestions?.[currentQuestionIndex ?? 0].id;
      if (!questionId) return;
      saveUserInputToSession(input, questionId);
    },
    [allQuestions, currentQuestionIndex],
  );

  // Loading state
  const loading = courseLoading || questionsLoading;

  // Helper functions for tracking attempts
  const incrementDebugHelp = useCallback(() => {
    setAttemptTracking((prev) => ({
      ...prev,
      debugHelpCount: prev.debugHelpCount + 1,
    }));
  }, []);

  const incrementExplainHelp = useCallback(() => {
    setAttemptTracking((prev) => ({
      ...prev,
      explainHelpCount: prev.explainHelpCount + 1,
    }));
  }, []);

  const incrementExplainHighlightedHelp = useCallback(() => {
    if (!currentQuestionId) return;

    const newCount = incrementExplainHighlightedUsage(currentQuestionId);
    setAttemptTracking((prev) => ({
      ...prev,
      explainHighlightedCount: newCount,
    }));
  }, [currentQuestionId]);

  const updateAttemptStatus = useCallback(
    (
      isCorrect: boolean,
      wpm: number | null = null,
      shouldIncrementAttempt = true,
    ) => {
      setAttemptTracking((prev) => ({
        ...prev,
        attemptCount: shouldIncrementAttempt
          ? prev.attemptCount + 1
          : prev.attemptCount,
        isCorrect,
        wpm: wpm ?? prev.wpm,
      }));
    },
    [],
  );

  const resetAttemptTracking = useCallback(() => {
    setAttemptTracking(defaultAttemptTracking);
  }, []);

  const resetExplainHighlightedUsageData = useCallback(() => {
    resetExplainHighlightedUsage();
    setAttemptTracking((prev) => ({
      ...prev,
      explainHighlightedCount: 0,
    }));
  }, []);

  // Load explain highlighted usage count when question changes
  useEffect(() => {
    if (currentQuestionId) {
      const usageCount =
        getExplainHighlightedUsageForQuestion(currentQuestionId);
      setAttemptTracking((prev) => ({
        ...prev,
        explainHighlightedCount: usageCount,
      }));
    }
  }, [currentQuestionId]);

  // Load user input from session storage when component mounts or question changes
  // useEffect(() => {
  //   const savedInput = getUserInputFromSession();
  //   if (savedInput && !userInput) {
  //     setUserInput(savedInput);
  //   }
  // }, [currentQuestionId, userInput]);

  // Reset attempt tracking when question changes - now after resetAttemptTracking is defined
  // useEffect(() => {
  //   // Use a more pragmatic approach with 'any' type to bypass TypeScript errors
  //   const currentQuestion = allQuestions?.[currentQuestionIndex ?? 0] as any;
  //   if (currentQuestion?.detail) {
  //     const questionDetail = currentQuestion.detail;

  //     // Initialize with previous attempt data if available
  //     setAttemptTracking({
  //       debugHelpCount: 0, // Reset help counts
  //       explainHelpCount: 0,
  //       attemptCount: questionDetail.attempt_count || 0,
  //       isCorrect: false, // We don't know if it was correct
  //       isCompleted: false,
  //       wpm: questionDetail.words_per_minute || null,
  //       isSubmitted: (questionDetail.attempt_count || 0) >= 3, // Consider submitted if max attempts reached
  //     });

  //     // Set previous code if available
  //     if (questionDetail.attempted_code) {
  //       const isTypingCourse = currentQuestion?.is_typing_course || false;
  //       if (!isTypingCourse) {
  //         setUserInput(questionDetail.attempted_code);
  //       }
  //     } else {
  //       // Only reset user input if there was no previous attempt
  //       setUserInput('');
  //     }
  //   } else {
  //     // No previous attempt data available, reset everything
  //     resetAttemptTracking();
  //     setUserInput('');
  //   }
  // }, [currentQuestionId, currentQuestionIndex, resetAttemptTracking]);

  // Determine if current question is the last one
  const isLastQuestion = useMemo(() => {
    if (!allQuestions || !currentQuestionId) return false;
    const currentIndex = allQuestions.findIndex(
      (q) => q.id === currentQuestionId,
    );
    return currentIndex === allQuestions.length - 1;
  }, [allQuestions, currentQuestionId]);

  // Popup message helpers
  const showPopupMessage = useCallback(
    (message: string, onConfirm?: () => void, confirmText?: string) => {
      setPopupMessage({
        isOpen: true,
        message,
        onConfirm,
        confirmText,
      });
    },
    [],
  );

  const hidePopupMessage = useCallback(() => {
    setPopupMessage((prev) => ({
      ...prev,
      isOpen: false,
    }));
  }, []);

  console.log('attemptTracking', attemptTracking);
  // Submit attempt to API
  const submitAttempt = useCallback(
    async (isCorrect: boolean, wpm: number | null = null) => {
      console.log('submitAttempt', currentQuestionId, isCorrect, wpm);
      if (!currentQuestionId) return;
      try {
        const currentAttemptCount = attemptTracking.attemptCount + 1;

        const isSubmitted = isCorrect || currentAttemptCount >= 10;

        setAttemptTracking((prev) => ({
          ...prev,
          attemptCount: currentAttemptCount,
          isCorrect,
          wpm: wpm ?? prev.wpm,
          isSubmitted,
        }));

        const currentQuestion = allQuestions?.[currentQuestionIndex];
        const isTypingCourse = currentQuestion?.is_typing_course || false;

        if (isCorrect) {
          if (isTypingCourse && typeof wpm === 'number') {
            showPopupMessage(
              `Correct, Awesome job! Your words per minute for this activity is: ${wpm.toFixed(1)}. Click 'Next' to keep the momentum going and tackle your next activity!`,
            );
          } else {
            showPopupMessage(
              'Great job! You got it right. Click Next to continue.',
            );
          }
        } else {
          if (currentAttemptCount >= 1 && currentAttemptCount <= 7) {
            let message =
              "Oops! That's not quite right. Try checking your submission to see if you can fix it. If you're still stuck, you can ask My Code Dragon AI to help you debug it!";
            if (isTypingCourse) {
              message =
                "Oops! That's not quite right. Try checking your submission to see if you can fix it.";
            }
            showPopupMessage(message);
          } else if (currentAttemptCount === 8) {
            let message =
              "Oops! That's not quite right. You have two more attempts. Try checking your submission to see if you can fix it. If you're still stuck, you can ask My Code Dragon AI to help you debug it!";
            if (isTypingCourse) {
              message =
                "Oops! That's not quite right. You have two more attempts. Try checking your submission to see if you can fix it.";
            }
            showPopupMessage(message);
          } else if (currentAttemptCount === 9) {
            let message =
              "Oops! That's not quite right. You have one more attempt. Try checking your submission to see if you can fix it. If you're still stuck, you can ask My Code Dragon AI to help you debug it!";
            if (isTypingCourse) {
              message =
                "Oops! That's not quite right. You have one more attempt. Try checking your submission to see if you can fix it.";
            }
            showPopupMessage(message);
          } else if (currentAttemptCount >= 10) {
            const message =
              "Oops! That one didn't work out, and you've used all your attempts. No worries, let's keep going! Click Next to tackle the next activity. You can always come back and retry this course in the future.";
            showPopupMessage(message);
          }
        }

        const response = await submitCourseAttempt({
          user: user || 0,
          question: currentQuestionId,
          attempted_code: userInput,
          words_per_minute: typeof wpm === 'number' ? wpm : 0,
          is_correct: isCorrect,
          is_completed: false,
          debug_help_count:
            attemptTracking.debugHelpCount && attemptTracking.debugHelpCount > 0
              ? true
              : false,
          explain_help_count:
            attemptTracking.explainHelpCount &&
            attemptTracking.explainHelpCount > 0
              ? true
              : false,
        });

        if (isCorrect || currentAttemptCount >= 10) {
          setAttemptTracking((prev) => ({
            ...prev,
            isSubmitted: true,
          }));
        }
      } catch (error) {
        console.error('Error submitting attempt:', error);
      }
    },
    [
      currentQuestionId,
      currentQuestionIndex,
      attemptTracking,
      userInput,
      showPopupMessage,
      submitCourseAttempt,
    ],
  );

  // Mark question as completed without checking correctness (when clicking next without submitting)
  const completeQuestion = useCallback(async () => {
    if (!currentQuestionId) return;

    try {
      // Update local state to mark as completed
      setAttemptTracking((prev) => ({
        ...prev,
        isCompleted: true,
      }));
    } catch (error) {
      console.error('Error completing question:', error);
    }
  }, [currentQuestionId, userInput, attemptTracking, submitCourseAttempt]);

  // Navigation function - now with completion tracking
  const nextQuestion = useCallback(
    async (updateProgress = true) => {
      if (!allQuestions) return;

      // If question hasn't been completed or marked correct, submit a completion
      if (!attemptTracking.isCompleted && !attemptTracking.isCorrect) {
        await completeQuestion();
      }

      // Reset attempt tracking for next question
      resetAttemptTracking();

      // Find current question index in all questions
      const currentIndex = allQuestions.findIndex(
        (q) => q.id === currentQuestionId,
      );

      // If there are more questions
      if (currentIndex < allQuestions.length - 1) {
        const nextQuestionData = allQuestions[currentIndex + 1];
        setCurrentQuestionId(nextQuestionData.id);
        const userInput = getUserInputFromSession(nextQuestionData.id);
        setUserInput(userInput);
      }

      // Update progress
      if (courseData?.detail && updateProgress) {
        const totalQuestions = courseData.detail.total_questions;
        const incrementProgress = 100 / totalQuestions;
        const newProgress =
          totalQuestions > 0 ? Math.round(incrementProgress) : 0;
        setProgress((prev: any) => (prev ? prev + newProgress : newProgress));
      }

      if (updateProgress) {
        // Clear user input for the next question
        setUserInput('');
        // Clear session storage when moving to next question
        clearUserInputFromSession();
      }

      // Hide any popup messages
      hidePopupMessage();
    },
    [
      allQuestions,
      courseId,
      currentQuestionId,
      attemptTracking.isCompleted,
      attemptTracking.isCorrect,
      completeQuestion,
      resetAttemptTracking,
      hidePopupMessage,
      courseData,
      setProgress,
    ],
  );

  useEffect(() => {
    return () => {
      console.log('clearing user input from session');
      clearUserInputFromSession();
    };
  }, []);

  const updateProgressTimeSpent = (currentTimeInMinutes: number) => {
    if (!courseId || !currentQuestionId) return;
    trackCourseProgress({
      course: courseId,
      question: currentQuestionId,
      time_spent: currentTimeInMinutes,
    });
  };

  // Prepare context value
  const contextValue: CourseContextType = {
    // Course info
    courseLoading,
    courseId,
    courseName: courseData?.course_name || '',
    courseDuration: courseData?.duration || '',
    progress: progress || 0,
    language: courseData?.language?.name || '',
    timeSpent: timeSpent || 0,
    // Current state
    currentQuestionId,
    allQuestions,
    currentQuestionIndex,
    isLastQuestion,
    userInput,
    loading,

    // Attempt tracking
    attemptTracking,
    updateProgressTimeSpent,
    incrementDebugHelp,
    incrementExplainHelp,
    incrementExplainHighlightedHelp,
    updateAttemptStatus,
    resetAttemptTracking,
    resetExplainHighlightedUsageData,
    clearUserInputFromSession,
    // Popup functionality
    popupMessage,
    showPopupMessage,
    hidePopupMessage,

    // Actions
    submitAttempt,
    completeQuestion,
    nextQuestion,
    setProgress,
    setTimeSpent,
    setCurrentQuestionId,
    setUserInput: setUserInputWithSession,
  };

  // Check if question has already been attempted and move to next question automatically
  useEffect(() => {
    if (!allQuestions || !currentQuestionId || !courseData) return;
    const lastQuestionDetails = courseData?.detail || undefined;
    if (!lastQuestionDetails) return;
    const currentQuestion = allQuestions.find(
      (q) => q.id === currentQuestionId,
    );
    if (lastQuestionDetails?.last_incomplete_question_id !== currentQuestionId)
      return;
    if (
      currentQuestion &&
      ((lastQuestionDetails?.last_incomplete_question_attempts ?? 0) >= 10 ||
        lastQuestionDetails?.is_correct)
    ) {
      const currentIndex = allQuestions.findIndex(
        (q) => q.id === currentQuestionId,
      );
      const isLastQuestion = currentIndex === allQuestions.length - 1;
      if (isLastQuestion) {
        setAttemptTracking((prev) => ({
          ...prev,
          attemptCount:
            lastQuestionDetails?.last_incomplete_question_attempts || 0,
          isCorrect: lastQuestionDetails?.is_correct || false,
        }));
        const userInput = getUserInputFromSession(currentQuestionId);
        setUserInput(
          userInput || (lastQuestionDetails as any)?.attempted_code || '',
        );
        return;
      }

      setTimeout(() => {
        nextQuestion(false);
      }, 0);
    } else if (
      (lastQuestionDetails?.last_incomplete_question_attempts ?? 0) >= 0 &&
      (lastQuestionDetails?.last_incomplete_question_attempts ?? 0) < 10
    ) {
      setAttemptTracking((prev) => ({
        ...prev,
        attemptCount:
          lastQuestionDetails?.last_incomplete_question_attempts || 0,
      }));
      console.log('courseData?.language?.name:', courseData?.language?.name);
      if (courseData?.language?.name !== 'typing') {
        console.log('setting user input');
        const userInput = getUserInputFromSession(currentQuestionId);
        setUserInput(
          userInput || (lastQuestionDetails as any)?.attempted_code || '',
        );
      }
    }
  }, [currentQuestionId, allQuestions, courseData]);

  return (
    <CourseContext.Provider value={contextValue}>
      {children}
    </CourseContext.Provider>
  );
};

// Custom hook to use the course context
export const useCourse = () => {
  return useContext(CourseContext);
};
